import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../home/components/Navbar';
import Footer from '../home/components/Footer';

const services = [
  {
    icon: 'ri-drop-line',
    title: 'Water Damage',
    path: '/services/water-damage',
    color: 'bg-teal-50',
    iconColor: 'text-teal-600',
    borderColor: 'border-teal-100',
    hoverBorder: 'hover:border-teal-400',
    image: 'https://public.readdy.ai/ai/img_res/3fc86c1c90bb07192d6a72de15690843.jpg',
    desc: 'Fast water extraction, structural drying, and full restoration after burst pipes, flooding, or appliance leaks.',
    bullets: ['Burst pipe & plumbing leaks', 'Appliance overflow cleanup', 'Basement & crawl space flooding', 'Storm & flood damage'],
  },
  {
    icon: 'ri-fire-line',
    title: 'Fire Damage',
    path: '/services/fire-damage',
    color: 'bg-orange-50',
    iconColor: 'text-orange-500',
    borderColor: 'border-orange-100',
    hoverBorder: 'hover:border-orange-400',
    image: 'https://readdy.ai/api/search-image?query=fire%20damage%20restoration%20crew%20cleaning%20smoke%20soot%20from%20walls%20inside%20burned%20home%20interior%20with%20professional%20equipment%2C%20photorealistic%2C%20clean%20simple%20background%2C%20bright%20work%20lights&width=600&height=400&seq=svc-fire-01&orientation=landscape',
    desc: 'Complete fire and smoke damage cleanup — from emergency board-up and debris removal to odor elimination and full rebuild.',
    bullets: ['Smoke & soot removal', 'Odor elimination', 'Board-up & tarping', 'Full structural reconstruction'],
  },
  {
    icon: 'ri-leaf-line',
    title: 'Mold Remediation',
    path: '/services/mold-remediation',
    color: 'bg-green-50',
    iconColor: 'text-green-600',
    borderColor: 'border-green-100',
    hoverBorder: 'hover:border-green-400',
    image: 'https://readdy.ai/api/search-image?query=mold%20remediation%20specialist%20in%20protective%20suit%20removing%20black%20mold%20from%20wall%20inside%20residential%20home%20with%20containment%20barriers%20and%20HEPA%20equipment%2C%20photorealistic%2C%20bright%20professional%20lighting&width=600&height=400&seq=svc-mold-01&orientation=landscape',
    desc: 'EPA-compliant mold inspection, containment, and removal to protect your family\'s health and your property\'s integrity.',
    bullets: ['Black mold removal', 'HEPA air filtration', 'Crawl space remediation', 'Post-remediation testing'],
  },
  {
    icon: 'ri-thunderstorms-line',
    title: 'Storm Damage',
    path: '/services/storm-damage',
    color: 'bg-sky-50',
    iconColor: 'text-sky-600',
    borderColor: 'border-sky-100',
    hoverBorder: 'hover:border-sky-400',
    image: 'https://readdy.ai/api/search-image?query=storm%20damage%20restoration%20team%20repairing%20roof%20and%20removing%20fallen%20tree%20debris%20from%20damaged%20California%20home%20exterior%20after%20severe%20storm%2C%20photorealistic%2C%20clean%20simple%20background%2C%20daylight&width=600&height=400&seq=svc-storm-01&orientation=landscape',
    desc: 'Emergency storm response including roof tarping, debris removal, water intrusion cleanup, and full property restoration.',
    bullets: ['Roof tarping & board-up', 'Debris removal', 'Water intrusion cleanup', 'Structural repairs'],
  },
  {
    icon: 'ri-building-2-line',
    title: 'Commercial',
    path: '/services/commercial',
    color: 'bg-slate-50',
    iconColor: 'text-slate-600',
    borderColor: 'border-slate-100',
    hoverBorder: 'hover:border-slate-400',
    image: 'https://readdy.ai/api/search-image?query=commercial%20property%20restoration%20team%20working%20in%20large%20office%20building%20with%20industrial%20drying%20equipment%20and%20dehumidifiers%20set%20up%20in%20open%20floor%20plan%2C%20photorealistic%2C%20professional%20lighting%2C%20clean%20background&width=600&height=400&seq=svc-commercial-01&orientation=landscape',
    desc: 'Minimizing downtime for businesses — we handle large-scale water, fire, mold, and storm damage in commercial properties.',
    bullets: ['Office & retail restoration', 'Warehouse & industrial', 'Multi-unit residential', 'Minimal business disruption'],
  },
  {
    icon: 'ri-tools-line',
    title: 'Reconstruction',
    path: '/services/reconstruction',
    color: 'bg-amber-50',
    iconColor: 'text-amber-600',
    borderColor: 'border-amber-100',
    hoverBorder: 'hover:border-amber-400',
    image: 'https://readdy.ai/api/search-image?query=skilled%20construction%20workers%20rebuilding%20interior%20of%20home%20after%20water%20damage%20restoration%20with%20new%20drywall%20framing%20and%20flooring%20installation%2C%20photorealistic%2C%20bright%20clean%20background%2C%20professional%20setting&width=600&height=400&seq=svc-recon-01&orientation=landscape',
    desc: 'Full interior and structural reconstruction after any disaster — drywall, flooring, painting, and everything in between.',
    bullets: ['Drywall & framing', 'Flooring replacement', 'Painting & finishing', 'Kitchen & bath rebuild'],
  },
];

export default function ServicesPage() {
  useEffect(() => {
    const siteUrl = import.meta.env.VITE_SITE_URL || 'https://mercedwaterrestoration.com';

    // Add Schema.org JSON-LD
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "Service",
      "serviceType": "Restoration Services",
      "provider": {
        "@type": "LocalBusiness",
        "name": "Indoor-Restore Services",
        "telephone": "+12094512288",
        "address": {
          "@type": "PostalAddress",
          "streetAddress": "1729 U St",
          "addressLocality": "Merced",
          "addressRegion": "CA",
          "postalCode": "95340",
          "addressCountry": "US"
        },
        "url": siteUrl
      },
      "areaServed": {
        "@type": "City",
        "name": "Merced",
        "containedInPlace": {
          "@type": "State",
          "name": "California"
        }
      },
      "hasOfferCatalog": {
        "@type": "OfferCatalog",
        "name": "Restoration Services",
        "itemListElement": services.map((svc, idx) => ({
          "@type": "Offer",
          "position": idx + 1,
          "itemOffered": {
            "@type": "Service",
            "name": svc.title,
            "description": svc.desc,
            "url": `${siteUrl}${svc.path}`
          }
        }))
      }
    });
    document.head.appendChild(script);

    return () => {
      document.head.removeChild(script);
    };
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero */}
      <section className="relative min-h-[55vh] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=professional%20restoration%20company%20team%20standing%20in%20front%20of%20service%20vehicles%20with%20equipment%20ready%20for%20emergency%20response%20in%20California%20suburban%20neighborhood%2C%20photorealistic%2C%20golden%20hour%20lighting%2C%20clean%20background&width=1920&height=800&seq=svc-hero-main-01&orientation=landscape"
            alt="Indoor-Restore Services - All Services Merced CA"
            className="w-full h-full object-cover object-top"
            loading="lazy"
            decoding="async"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/50 to-black/65"></div>
        </div>
        <div className="relative z-10 text-center px-4 w-full max-w-4xl mx-auto py-24">
          <span className="inline-block bg-teal-500/30 border border-teal-400/50 text-teal-200 text-sm font-semibold px-4 py-1.5 rounded-full mb-5">
            Merced, CA &amp; Central Valley
          </span>
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-5 leading-tight">
            Our Restoration Services
          </h1>
          <p className="text-xl text-white/85 mb-8 max-w-2xl mx-auto">
            From water and fire damage to mold, storms, and full reconstruction — Indoor-Restore Services handles it all with certified technicians and 24/7 emergency response.
          </p>
          <a
            href="tel:2094512288"
            className="inline-flex items-center gap-2 bg-teal-600 text-white px-8 py-4 rounded-lg text-lg font-semibold hover:bg-teal-700 transition-all shadow-lg whitespace-nowrap cursor-pointer"
          >
            <i className="ri-phone-line"></i>Call (209) 451-2288
          </a>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">What We Do</h2>
            <p className="text-gray-500 max-w-2xl mx-auto text-lg">
              Click any service below to learn more about our process, what's included, and how we can help you recover fast.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((svc) => (
              <Link
                key={svc.title}
                to={svc.path}
                className={`group rounded-2xl border-2 ${svc.borderColor} ${svc.hoverBorder} bg-white shadow-sm hover:shadow-lg transition-all duration-300 overflow-hidden cursor-pointer`}
              >
                <div className="w-full h-52 overflow-hidden">
                  <img
                    src={svc.image}
                    alt={svc.title}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                    loading="lazy"
                    decoding="async"
                  />
                </div>
                <div className="p-7">
                  <div className="flex items-center gap-3 mb-4">
                    <div className={`w-11 h-11 ${svc.color} rounded-lg flex items-center justify-center flex-shrink-0`}>
                      <i className={`${svc.icon} text-2xl ${svc.iconColor}`}></i>
                    </div>
                    <h3 className="text-xl font-bold text-gray-900">{svc.title}</h3>
                  </div>
                  <p className="text-gray-500 text-sm leading-relaxed mb-5">{svc.desc}</p>
                  <ul className="space-y-2 mb-6">
                    {svc.bullets.map((b) => (
                      <li key={b} className="flex items-center gap-2 text-gray-600 text-sm">
                        <div className="w-4 h-4 flex items-center justify-center flex-shrink-0">
                          <i className="ri-checkbox-circle-fill text-teal-500"></i>
                        </div>
                        {b}
                      </li>
                    ))}
                  </ul>
                  <span className={`inline-flex items-center gap-1.5 text-sm font-semibold ${svc.iconColor} group-hover:gap-3 transition-all`}>
                    Learn More <i className="ri-arrow-right-line"></i>
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Trust Bar */}
      <section className="py-16 bg-slate-50 border-y border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            {[
              { icon: 'ri-time-line', label: '60-Min Response', sub: 'Anywhere in Merced County' },
              { icon: 'ri-award-line', label: 'IICRC Certified', sub: 'Highest industry standards' },
              { icon: 'ri-file-list-3-line', label: 'Insurance Billing', sub: 'We handle the paperwork' },
              { icon: 'ri-shield-check-line', label: '5-Star Rated', sub: 'Trusted by hundreds of clients' },
            ].map((item) => (
              <div key={item.label} className="flex flex-col items-center gap-3">
                <div className="w-14 h-14 bg-teal-50 rounded-full flex items-center justify-center">
                  <i className={`${item.icon} text-2xl text-teal-600`}></i>
                </div>
                <p className="font-bold text-gray-900 text-base">{item.label}</p>
                <p className="text-gray-500 text-sm">{item.sub}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-gradient-to-br from-teal-600 to-teal-700">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <h2 className="text-4xl font-bold text-white mb-4">Need Emergency Help Right Now?</h2>
          <p className="text-white/80 mb-8 text-lg">Our team is available 24/7 across Merced, CA and the entire Central Valley. Don't wait — call us immediately.</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href="tel:2094512288" className="bg-white text-teal-700 px-10 py-4 rounded-lg text-lg font-bold hover:bg-gray-100 transition-all shadow-lg whitespace-nowrap cursor-pointer">
              <i className="ri-phone-line mr-2"></i>(209) 451-2288
            </a>
            <a href="mailto:info@mercedwaterrestoration.com" className="border-2 border-white text-white px-10 py-4 rounded-lg text-lg font-bold hover:bg-white/10 transition-all whitespace-nowrap cursor-pointer">
              Email Us
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
